import {
  createAsyncThunk,
  createSlice,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import {
  fetchManagerPendingRequestsApi,
  updateManagerPendingRequestApi,
} from "../../api/warehouse.api";
import { UnitType } from "../../models/warehouseRawMaterial.model";

export interface IHistory {
  status: string;
  date: string;
  remark: string;
  userId: { _id: string; username: string };
  suggestedQuantity?: number;
  suggestedUnit?: UnitType;
  fulfilledQuantity?: number;
  _id?: string;
}

export interface ManagerFloorInwardRequest {
  _id?: string;
  floorRequestId?: number;
  productId: { _id: string; name: string; unit: UnitType; unitType: string; };
  userId?: { _id: string; username: string } | string;
  warehouseId?: string;
  quantity: number;
  unit: UnitType;
  status: string;
  remark?: string;
  createdAt?: string;
  updatedAt?: string;
  suggestedQuantity?: number;
  suggestedUnit?: UnitType;
  materialName?: string;
  history: IHistory[];
  __v?: number;
}

export interface Lot {
  lotId: number;
  requests: ManagerFloorInwardRequest[];
  totalRequests: number;
}

export interface PaginatedLotResponse {
  data: Lot[];
  totalLots: number;
  totalLotPages: number;
  lotPage: number;
}

export interface ManagerWarehouseState {
  pendingLots: Lot[];
  totalLots: number;
  totalLotPages: number;
  loading: boolean;
  error: string | null;
}

const initialState: ManagerWarehouseState = {
  pendingLots: [],
  totalLots: 0,
  totalLotPages: 0,
  loading: false,
  error: null,
};

export const fetchManagerPendingRequests = createAsyncThunk<
  PaginatedLotResponse,
  { lotPage: number; lotLimit: number; search?: string },
  { rejectValue: string }
>(
  "managerWarehouse/fetchManagerPendingRequests",
  async ({ lotPage, lotLimit, search }, { rejectWithValue }) => {
    try {
      return await fetchManagerPendingRequestsApi(lotPage, lotLimit, search);
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch pending requests"
      );
    }
  }
);

export const updateManagerPendingRequest = createAsyncThunk<
  void,
  {
    id: string;
    status: string;
    remark?: string;
    suggestedQuantity?: number;
    suggestedUnit?: UnitType;
    fulfilledQuantity?: number;
  }[],
  { rejectValue: string }
>(
  "managerWarehouse/updateManagerPendingRequest",
  async (payloadArray, { rejectWithValue }) => {
    try {
      await updateManagerPendingRequestApi(payloadArray);
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update requests"
      );
    }
  }
);

const managerWarehouseSlice = createSlice({
  name: "managerWarehouse",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<ManagerWarehouseState>) => {
    builder
      .addCase(fetchManagerPendingRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchManagerPendingRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingLots = action.payload.data || [];
        state.totalLots = action.payload.totalLots ?? 0;
        state.totalLotPages = action.payload.totalLotPages ?? 0;
      })
      .addCase(fetchManagerPendingRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unknown error";
        state.pendingLots = [];
      })
      .addCase(updateManagerPendingRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateManagerPendingRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateManagerPendingRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unknown error";
      });
  },
});

export const { clearError } = managerWarehouseSlice.actions;
export default managerWarehouseSlice.reducer;
